import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AngularFirestore } from '@angular/fire/firestore';
import { Account, Tenant, VoucherSettings, VoucherResetPeriod } from 'src/models/account.model';
import { User } from 'src/models/user.model';
import { AuthService } from 'src/services/auth.service';
import { TenantService } from 'src/services/tenant.service';
import { SettingsService } from 'src/services/settings.service';
import { AccountService } from 'src/services/account.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-add-tenant',
  templateUrl: './add-tenant.component.html'
})
export class AddTenantComponent implements OnInit {

  tenantForm: FormGroup;
  account: Account;
  userAuth: User;
  existingUsernames: string[] = [];
  showPin: boolean = false;

  freeHoursOptions = [1, 2, 3, 4, 5, 6, 7, 8];
  expirationOptions = [
    { value: 12, label: '12 hours' },
    { value: 24, label: '24 hours' },
    { value: 48, label: '48 hours' },
    { value: 72, label: '72 hours (3 days)' },
    { value: 168, label: '168 hours (7 days)' }
  ];
  resetPeriodOptions: { value: VoucherResetPeriod; label: string }[] = [
    { value: 'none', label: 'No Reset (Lifetime Limit)' },
    { value: 'daily', label: 'Daily (Resets every day)' }
  ];

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private firestore: AngularFirestore,
    public auth: AuthService,
    private tenantService: TenantService,
    private settingsService: SettingsService,
    private accountService: AccountService
  ) {
    this.account = JSON.parse(localStorage.getItem('account'));
    this.initializeForm();
    this.initializeExistingUsernames();
  }

  ngOnInit() {
    this.auth.user$.subscribe(user => {
      this.userAuth = user;
    });
  }

  initializeForm() {
    this.tenantForm = this.formBuilder.group({
      tenant_id: [this.firestore.createId()],
      store_name: ['', Validators.required],
      tenant_name: ['', Validators.required],
      phone_number: [''],
      email: ['', Validators.email],
      username: [''],
      password_pin: [''],
      free_parking_hours: [1, Validators.required],
      generation_limit: [null],
      unlimited_vouchers: [true],
      expiration_hours: [24, Validators.required],
      reset_period: ['none'],
      is_active: [true],
      date_created: [new Date()]
    });
  }

  initializeExistingUsernames() {
    if (this.account?.settings?.tenants) {
      this.existingUsernames = this.account.settings.tenants.map(t => t.username);
    }
  }

  get f() {
    return this.tenantForm.controls;
  }

  toggleShowPin() {
    this.showPin = !this.showPin;
  }

  onUnlimitedChange() {
    const unlimited = this.tenantForm.get('unlimited_vouchers').value;
    if (unlimited) {
      this.tenantForm.patchValue({ generation_limit: null });
    }
  }

  async generateUsernamePassword() {
    const storeName = this.tenantForm.get('store_name').value;
    const tenantName = this.tenantForm.get('tenant_name').value;

    if (!storeName || !tenantName) {
      await Swal.fire({
        icon: 'warning',
        title: 'Missing Information',
        text: 'Please enter Store Name and Tenant Name first.'
      });
      return;
    }

    const result = await Swal.fire({
      title: 'Generate Credentials',
      text: 'This will generate a username and PIN for the tenant.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Generate',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      this.generate();
    }
  }

  generate() {
    const storeName = this.tenantForm.get('store_name').value;
    const tenantName = this.tenantForm.get('tenant_name').value;

    let username = this.tenantService.createUsername(storeName, tenantName);

    // Check if username already exists, append number if needed
    let counter = 1;
    let originalUsername = username;
    while (this.existingUsernames.includes(username)) {
      username = `${originalUsername}${counter}`;
      counter++;
    }

    const pin = this.tenantService.generatePin();

    this.tenantForm.patchValue({
      username: username,
      password_pin: pin
    });

    Swal.fire({
      title: 'Credentials Generated!',
      html: `
        <div style="text-align: left;">
          <p><strong>Username:</strong> ${username}</p>
          <p><strong>PIN:</strong> ${pin}</p>
        </div>
        <p class="text-sm text-gray-500 mt-4">Please save these credentials. The PIN can be regenerated later if needed.</p>
      `,
      icon: 'success',
      confirmButtonText: 'OK'
    });
  }

  async save() {
    if (this.tenantForm.invalid) {
      await Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Please fill in all required fields.'
      });
      return;
    }

    const username = this.tenantForm.get('username').value;
    const pin = this.tenantForm.get('password_pin').value;

    if (!username || !pin) {
      await Swal.fire({
        icon: 'warning',
        title: 'Missing Credentials',
        text: 'Please generate username and PIN first.'
      });
      return;
    }

    // Check for duplicate username
    if (this.tenantService.isUsernameExists(this.account, username)) {
      await Swal.fire({
        icon: 'error',
        title: 'Username Exists',
        text: 'This username already exists. Please regenerate credentials.'
      });
      return;
    }

    await Swal.fire({
      title: 'Creating tenant...',
      allowEscapeKey: false,
      allowOutsideClick: false,
      showConfirmButton: false,
      didOpen: async () => {
        Swal.showLoading();

        try {
          const formData = this.tenantForm.value;

          const voucherSettings: VoucherSettings = {
            free_parking_hours: parseInt(formData.free_parking_hours),
            generation_limit: formData.unlimited_vouchers ? null : parseInt(formData.generation_limit),
            vouchers_generated: 0,
            expiration_hours: parseInt(formData.expiration_hours),
            reset_period: formData.unlimited_vouchers ? 'none' : formData.reset_period
          };

          const tenant: Tenant = {
            tenant_id: formData.tenant_id,
            account_key: this.account.account_key,
            store_name: formData.store_name,
            tenant_name: formData.tenant_name,
            phone_number: formData.phone_number,
            email: formData.email,
            username: formData.username,
            password_pin: formData.password_pin,
            voucher_settings: voucherSettings,
            is_active: formData.is_active,
            date_created: new Date()
          };

          await this.tenantService.addTenant(this.account, tenant);

          // Refresh account in localStorage
          const updatedAccount = await this.accountService.getAccountByKey(this.account.account_key);
          localStorage.setItem('account', JSON.stringify(updatedAccount));

          Swal.fire({
            title: 'Success!',
            text: 'Tenant created successfully.',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false
          }).then(() => {
            this.router.navigate([this.account.account_key + '/user/tenants']);
          });

        } catch (error) {
          console.error('Error creating tenant:', error);
          Swal.fire({
            title: 'Error',
            text: 'Failed to create tenant. Please try again.',
            icon: 'error'
          });
        }
      }
    });
  }

  cancel() {
    this.router.navigate([this.account.account_key + '/user/tenants']);
  }
}
